import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Instagram, MessageCircle, CheckCircle2 } from "lucide-react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Eyebrow, FAQ } from "./publicUI";
import { COMPANY, OFFICES, FAQS } from "./siteContent";

export default function Contact() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await client.post("/public/enquiry", {
        company_name: `${form.first_name} ${form.last_name}`.trim(),
        contact_person: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email, phone: form.phone, message: form.message, source: "contact-page",
      });
      setSent(true);
      toast.success("Message sent — our team will get back to you shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="relative brand-gradient-navy overflow-hidden">
        <div className="absolute inset-0 brand-dotgrid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-300 mb-3">Contact Us</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05]">Let's build your workforce</h1>
          <p className="mt-5 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">{COMPANY.positioning}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-start">
        {/* FORM */}
        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm order-2 lg:order-1">
          {sent ? (
            <div className="text-center py-12" data-testid="contact-success">
              <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
              <h3 className="font-display text-2xl font-bold text-[#0A192F] mt-4">Thank you!</h3>
              <p className="text-slate-500 mt-2">Your message has been received. Our team will reach out shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <h3 className="font-display text-xl font-bold text-[#0A192F]">Book an Appointment</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>First Name *</Label><Input required data-testid="contact-first" value={form.first_name} onChange={set("first_name")} /></div>
                <div><Label>Last Name *</Label><Input required data-testid="contact-last" value={form.last_name} onChange={set("last_name")} /></div>
                <div><Label>Email *</Label><Input required type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} /></div>
                <div><Label>Phone *</Label><Input required data-testid="contact-phone" value={form.phone} onChange={set("phone")} /></div>
              </div>
              <div><Label>Message *</Label><Textarea required data-testid="contact-message" value={form.message} onChange={set("message")} rows={4} /></div>
              <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full bg-[#0088FF] hover:bg-[#0070E0] h-11">
                {loading ? "Sending..." : "Get in touch now"}
              </Button>
            </form>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={COMPANY.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"><Instagram className="h-4 w-4 text-[#0088FF]" /> Instagram</a>
            <a href={COMPANY.whatsappChannel} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"><MessageCircle className="h-4 w-4 text-emerald-500" /> WhatsApp Channel</a>
          </div>
        </div>

        {/* OFFICES */}
        <div className="order-1 lg:order-2">
          <Eyebrow>Our Offices</Eyebrow>
          <h2 className="font-display text-3xl font-bold text-[#0A192F] mt-2">Fueling ambition. Every day.</h2>
          <div className="mt-6 space-y-5">
            {OFFICES.map((o) => (
              <div key={o.region} className="bg-white rounded-2xl border border-slate-200 p-6 hover-lift" data-testid={`office-${o.region.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display font-semibold text-[#0A192F]">{o.region}</div>
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium"><Clock className="h-3.5 w-3.5" /> {o.hours}</span>
                </div>
                <div className="text-sm text-slate-500 mt-1">{o.name}</div>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex gap-2.5 text-slate-600"><MapPin className="h-4 w-4 text-[#0088FF] shrink-0 mt-0.5" /> {o.address}</div>
                  <a href={`tel:${o.phone.replace(/\s/g, "")}`} className="flex gap-2.5 text-slate-600 hover:text-[#0088FF]"><Phone className="h-4 w-4 text-[#0088FF] shrink-0" /> {o.phone}</a>
                  <a href={`mailto:${o.email}`} className="flex gap-2.5 text-slate-600 hover:text-[#0088FF]"><Mail className="h-4 w-4 text-[#0088FF] shrink-0" /> {o.email}</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
          <iframe title="Emira Delhi office" width="100%" height="360" style={{ border: 0 }} loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=Shastri+Nagar+Delhi+110052&output=embed" />
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <div className="text-center max-w-2xl mx-auto mb-10"><Eyebrow>Emira Manpower &amp; Training Solutions</Eyebrow>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">Frequently Asked Questions</h2></div>
        <FAQ items={FAQS} />
      </section>
    </div>
  );
}
